import { addHours, isToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Variant } from '~/api/Catalog';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { formatVolume } from '~/components/ProductCard/utils';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { useProductLimit } from '~/hooks/useProductLimit';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, catalogStore, mainStore, orderStore } from '~/stores';
import { type Offer, Product } from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { isProductBundle } from '~/types/Product';
import {
  calcDiscountPercentage,
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';

export const useProduct = (product: Product, selectedOffer: Offer | null) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const [offer, setOffer] = useState(selectedOffer || product.offers[0]);

  useEffect(() => {
    if (selectedOffer || product) {
      setOffer(selectedOffer || product.offers[0]);
      setImage(
        selectedOffer?.previewImageThumb || currentItem.previewImageThumb,
      );
    }
  }, [product, selectedOffer]);

  const isBundle = isProductBundle(product);

  const isFavorite = favoritesStore.isFavorited(product.id);
  const { openModal } = useModal();

  const currentItemWithoutProperties = isBundle ? product : offer || product;

  const propertyFromVariant = product.offers?.find(
    ({ sku }) => sku === currentItemWithoutProperties.sku,
  );
  const propertiesFromVariant = {};
  if (propertyFromVariant) {
    propertiesFromVariant[`${propertyFromVariant.property}`] = {
      ...currentItemWithoutProperties.properties[propertyFromVariant.property],
      value: propertyFromVariant.value,
    };
  }
  if (Object.keys(propertiesFromVariant).length > 0) {
    // temporarily substitute properties if needed, but keep class instance
    // Use Object.defineProperty to override properties field if needed, non-intrusively
    Object.defineProperty(currentItemWithoutProperties, 'properties', {
      value: {
        ...currentItemWithoutProperties.properties,
        ...propertiesFromVariant,
      },
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  const currentItem = currentItemWithoutProperties;

  const [image, setImage] = useState(currentItem.previewImageThumb);
  const { limit, currentWhSellable, parentWhSellable } = useProductLimit({
    offer: currentItem,
  });

  const cartItemsCount = cartStore.getCartItemCountById(currentItem);
  const lowestMultibuyLevel = currentItem.multibuyPriceLevels?.length
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const isMultiBuy = !isBundle && Boolean(lowestMultibuyLevel);
  const applicableLevel = currentItem.multibuyPriceLevels?.length
    ? getApplicableMultibuyLevel(
        currentItem.multibuyPriceLevels,
        cartItemsCount,
      )
    : null;

  const isMultiBuyActive = Boolean(isMultiBuy && applicableLevel !== null);

  const currentPrice =
    isMultiBuyActive && applicableLevel
      ? convertPenceToPounds(applicableLevel.unitPrice)
      : currentItem.discountPrice
        ? currentItem.discountPriceFormatted
        : currentItem.priceFormatted;

  const oldPrice = currentItem.discountPrice
    ? currentItem.priceFormatted
    : null;

  const multiBuyInfo = useMemo(() => {
    if (
      !isMultiBuy ||
      !lowestMultibuyLevel ||
      !currentItem.multibuyPriceLevels?.length
    ) {
      return null;
    }

    const sortedLevels = sortMultibuyLevels(currentItem.multibuyPriceLevels);
    const isActive = applicableLevel !== null;

    // Always find next unreached level (even if multibuy is active)
    // This allows showing remainingCount to the next level after reaching current one
    const nextLevel = sortedLevels.find(
      (level: (typeof sortedLevels)[0]) => cartItemsCount < level.quantity,
    );

    // Use applicable level if active, otherwise use next level or lowest level
    const levelToUse = applicableLevel || nextLevel || lowestMultibuyLevel;
    const promoPrice = levelToUse.unitPrice;
    const promoQuantity = levelToUse.quantity;

    if (!promoPrice || promoPrice <= 0) {
      return null;
    }

    // Calculate remaining count from next unreached level, or 0 if all levels are reached
    const remainingCount = nextLevel
      ? Math.max(0, nextLevel.quantity - cartItemsCount)
      : 0;

    const basePrice = currentItem.price;

    if (basePrice <= 0 || promoPrice >= basePrice) {
      return null;
    }

    const discountPercent = calcDiscountPercentage(basePrice, promoPrice);

    // Calculate discount percent for next level if it exists
    const nextDiscountPercent = nextLevel
      ? (() => {
          const nextPromoPrice = nextLevel.unitPrice;
          if (
            nextPromoPrice <= 0 ||
            basePrice <= 0 ||
            nextPromoPrice >= basePrice
          ) {
            return null;
          }
          return calcDiscountPercentage(basePrice, nextPromoPrice);
        })()
      : null;

    return {
      remainingCount,
      discountPercent,
      discountValue: formatPriceWithCurrency(
        mainStore.convertPenceToPounds(
          (basePrice - promoPrice) * promoQuantity,
        ),
      ),
      nextDiscountPercent,
      nextDiscountValue: nextLevel
        ? formatPriceWithCurrency(
            mainStore.convertPenceToPounds(
              (basePrice - nextLevel.unitPrice) * nextLevel.quantity,
            ),
          )
        : null,
      isActive,
    };
  }, [
    isMultiBuy,
    lowestMultibuyLevel,
    applicableLevel,
    currentItem.multibuyPriceLevels,
    currentItem.discountPrice,
    currentItem.price,
    cartItemsCount,
  ]);

  const isExpress =
    currentItem &&
    currentWhSellable &&
    currentWhSellable >= cartItemsCount &&
    orderStore.isExpressAvailableNow;
  const isBackorderAvailable =
    currentItem &&
    currentItem.isBackorderAvailable &&
    !!currentItem.backorderLeadTime &&
    parentWhSellable > 0;

  const leadTime = currentItem.backorderLeadTime
    ? currentItem.backorderLeadTime
    : 0;

  const deliveryToday = currentItem.backorderLeadTime
    ? isToday(addHours(new Date(), currentItem.backorderLeadTime))
    : false;

  const backorderDate = currentItem.backorderLeadTime
    ? format(addHours(new Date(), currentItem.backorderLeadTime), 'd MMMM', {
        timeZone: company.config.timeZone,
        locale,
      })
    : null;

  const productHasVariants = useMemo(() => {
    return product.variants?.length
      ? product.variants.map(({ sku, value }) => ({ id: sku, value }))
      : product.offers.map((item) => ({
          id: item.sku,
          value: formatVolume(item.properties),
        }));
  }, [product.variants, product.offers]).length;

  const productHasManyVariants = productHasVariants > 1;

  const colorVariants = useMemo(
    () =>
      Object.values(
        product.variants?.reduce((acc: Record<string, Variant>, item) => {
          if (item.property === 'color' && !acc[item.value]) {
            acc[item.value] = item;
          }
          return acc;
        }, {}) || {},
      ).sort((a, b) => a.sorting - b.sorting),
    [product.variants],
  );

  const volumes = product.offers?.map((item) => ({
    id: item.sku,
    value: formatVolume(item.properties),
  })) || [{ id: 'unknown', value: company.config.defaultVolumeDisplay }];

  const propertyList = Object.values(selectedOffer?.properties ?? {})
    .filter(
      ({ type, code }) =>
        type !== 'toggle' && code !== 'volume' && type !== 'badge',
    )
    .sort((a, b) => a.order - b.order);

  const selectOffer = useCallback(
    (sku: string) => {
      const newOffer = product.offers.find((item) => item.sku === sku);

      if (newOffer) {
        setImage(newOffer.previewImageThumb);
        setOffer(newOffer);
        return;
      }

      const variant = product.variants?.find((variant) => variant.sku === sku);

      if (!variant) {
        return;
      }

      const { thumb, url } = (variant.images || []).reduce<{
        thumb: string;
        url: string;
      }>(
        (acc, { thumb, url }) => {
          if (thumb && !acc.thumb) {
            acc.thumb = thumb;
          }

          if (url && !acc.url) {
            acc.url = url;
          }

          return acc;
        },
        { thumb: '', url: '' },
      );

      if (thumb || url) {
        setImage(thumb || url);
      }

      Product.loadOffer(product, sku).then((offersMap) => {
        const newOffer = offersMap.get(sku);
        if (newOffer) {
          setImage(newOffer.previewImageThumb);
          setOffer(newOffer);
        }
      });
    },
    [product],
  );

  const toggleFavorite = useCallback(() => {
    favoritesStore.toggleFavorite(product, 'product_main');
    mainStore.sendToRN('hapticFeedback', {
      count: 1,
      type: 'soft',
    });
  }, [product]);

  const handleCopyCode = useCallback(async () => {
    const url = `${window.location.origin}/p/${product.slug}?sku=${currentItem.sku}`;
    try {
      await navigator.clipboard.writeText(url);
      mainStore.pushAlert('success', t('linkCopied'));
    } catch (error) {
      error && console.error(error);
      mainStore.sendToRN(
        'copyToClipboard',
        url,
        () => {
          mainStore.pushAlert('success', t('referral:promoCopied'));
        },
        () => {
          mainStore.pushAlert('error', t('errors:unknown'));
        },
      );
    }
    analyticsService.track(AnalyticsEvent.REFERRAL_PROGRAM_COPY_CODE, {});
  }, [t, product]);

  const changeCount = useCallback(
    (count: number, action: 'add' | 'remove') => {
      cartStore.setCartItemCountByProduct(
        {
          ...currentItem,
          variants: product.variants,
          ratingAverage: currentItem?.ratingAverage ?? 0,
          ratingMarksCount: currentItem?.ratingMarksCount ?? 0,
        },
        count,
        action,
        'product_main',
      );
    },
    [currentItem],
  );

  const addMultiBuyProductToCart = useCallback(() => {
    const targetCount =
      (currentItem.getLowestMultibuyLevel()?.quantity ?? 0) - cartItemsCount;

    if (targetCount <= 0 || limit < cartItemsCount + targetCount) {
      return;
    }

    changeCount(cartItemsCount + targetCount, 'add');
  }, [currentItem, changeCount, cartItemsCount, limit]);

  const openSelectSKUModal = useCallback(() => {
    catalogStore.setSelectedCatalogProduct(product.slug);
    openModal(ModalType.SelectSKU);
  }, [product]);

  return {
    currentItem,
    volumes,
    propertyList,
    limit,
    image,
    leadTime,
    oldPrice,
    currentPrice: formatPriceWithCurrency(currentPrice),
    multiBuyInfo,
    cartItemsCount,
    isMultiBuy,
    isExpress,
    isBackorderAvailable,
    isBundle,
    isFavorite,
    deliveryToday,
    backorderDate,
    productHasVariants,
    productHasManyVariants,
    colorVariants: [...new Set(colorVariants)],
    selectOffer,
    toggleFavorite,
    handleCopyCode,
    changeCount,
    addMultiBuyProductToCart,
    openSelectSKUModal,
  };
};
